/**
 * Hashtags: what counts as one, in one place.
 *
 * **Shared on purpose.** The client paints a hashtag blue and the server indexes it,
 * and if the two disagreed about where a tag starts or ends then the blue text would be
 * a lie about what was actually indexed — you would click `#ไปทะเลกัน` and land on a
 * page for `#ไปทะเล`. One pattern, imported by both.
 */

export const HASHTAG_LIMITS = {
  /** Characters after the `#`. Long enough for a Thai phrase, short of a paragraph. */
  tagMax: 60,
  /** Per post. A post with forty tags is not about forty things. */
  perPostMax: 10,
  /** How far back trending looks. Stated on the panel rather than implied. */
  trendingWindowDays: 7,
  /** How many the panel shows. */
  trendingMax: 10,
  /**
   * How fast a post stops counting towards trending.
   *
   * The window alone cannot rank: counted flat over seven days, fifty posts
   * last Tuesday beat forty in the last hour, and a panel that answers "what
   * is happening" with last Tuesday is answering a different question.
   *
   * So each post is weighted `0.5 ^ (age / halfLife)`. At twenty-four hours a
   * post from this morning counts about 1, yesterday about 0.5, and a week ago
   * about 0.004 — present but no longer deciding anything. A day is the right
   * figure for a product where a conversation is the unit: short enough that
   * today wins, long enough that a tag does not vanish while people are asleep
   * in the other half of the world.
   */
  trendingHalfLifeHours: 24,
  /**
   * How long a computed list is served before it is recomputed.
   *
   * A minute is well inside what anybody would notice on a panel of ten tags,
   * and it is the difference between one aggregate a minute and one per reader.
   */
  trendingFreshSeconds: 60,
  /**
   * How long a computed list may still be *served* while a fresh one is being
   * built.
   *
   * Longer than `trendingFreshSeconds` on purpose: past the first value, a
   * reader never waits for the aggregate again. The last list is handed over
   * immediately and the recomputation happens behind the response.
   *
   * **Two minutes and not ten, and the reason is privacy rather than
   * freshness.** This list is partly a privacy surface: the comment on
   * `post_hashtags` says a count that included private posts would publish the
   * existence of what the tag page refuses to show. A cached list keeps
   * publishing a tag for as long as it is served after the post carrying it
   * became unreadable — a group turned private, a post deleted. That window
   * cannot be zero without giving up the cache, so it is made short and
   * stated here instead of being discovered later.
   */
  trendingStaleSeconds: 120,
} as const;

/**
 * What a hashtag looks like.
 *
 * `\p{L}` and `\p{M}` rather than `\w`, and the `u` flag with them: **Thai has no
 * spaces**, so `#ไปทะเลกัน` is one tag that runs until punctuation or a space, and the
 * combining tone marks above and below Thai letters are `\p{M}` — a class without them
 * cuts a word in half mid-syllable.
 *
 * The lookbehind keeps two things out:
 *
 *  - **A URL fragment.** `https://example.com/#section` is not a tag, and the `/`
 *    before the `#` is what says so.
 *  - **A `#` inside a word.** `C#` and `item#3` are not tags either.
 *
 * A tag of digits alone is excluded by the pattern requiring at least one letter: `#1`
 * is the number one, and `#2025` is a year somebody wrote, not a topic.
 */
export const HASHTAG_PATTERN =
  /(?<![\p{L}\p{M}\p{N}_/#])#(?=[\p{L}\p{M}\p{N}_]*[\p{L}])([\p{L}\p{M}\p{N}_]{1,60})/gu;

/** A fresh regex per call: `lastIndex` is state, and a shared /g one answers differently the second time. */
export const hashtagPattern = (): RegExp => new RegExp(HASHTAG_PATTERN.source, HASHTAG_PATTERN.flags);

/**
 * Normalises a tag to the form it is stored and looked up under.
 *
 * Lower-cased so `#Figma` and `#figma` are one topic — which is what anybody typing
 * either of them means. Thai has no case, so this is a no-op for half the tags here and
 * harmless for the rest.
 */
export const normalizeHashtag = (tag: string): string => tag.replace(/^#+/, '').toLowerCase();

/**
 * Every tag in a piece of text, normalised, de-duplicated, in the order written.
 *
 * Capped rather than refused: a post with thirty tags is still a post, and dropping the
 * thirtieth is kinder than rejecting what somebody wrote.
 */
export function parseHashtags(text: string): string[] {
  if (!text) return [];
  const seen = new Set<string>();
  const pattern = hashtagPattern();

  for (let match = pattern.exec(text); match !== null; match = pattern.exec(text)) {
    const tag = normalizeHashtag(match[1] ?? '');
    if (tag.length === 0 || tag.length > HASHTAG_LIMITS.tagMax) continue;
    seen.add(tag);
    if (seen.size >= HASHTAG_LIMITS.perPostMax) break;
  }
  return [...seen];
}
